import {
  flexColumnWidthBasis,
  normalizeFlexWidthValue,
} from "../../utils/builderUtils";

const pxValue = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const str = v.toString();
  return /px$/.test(str) ? str : `${str}px`;
};

// Builder dropdowns store these as keys ("top-center", "expand-height");
// CSS needs space-separated keywords and explicit sizing syntax. "custom"
// (only reachable on columns upgraded from a legacy section) returns null so
// the caller emits explicit background-position-x/y offsets instead.
const toCssBackgroundPosition = (value) =>
  value === "custom" ? null : (value || "center center").replace("-", " ");

// Strip the characters that could break out of the url("…") context and inject
// arbitrary CSS into the live page (background is a user/builder-stored value).
const sanitizeCssUrl = (value) =>
  value == null ? value : String(value).replace(/["\\\r\n]/g, "");

const toCssBackgroundSize = (value) => {
  switch (value) {
    case "expand-height":
      return "auto 100%";
    case "expand-width":
      return "100% auto";
    default:
      return value || "cover";
  }
};

const flexRuleFor = ({ value, grow }) =>
  grow
    ? "flex: 1 1 1px;\n      min-width: 0;\n      max-width: none;\n      box-sizing: border-box;"
    : `flex: 0 0 ${value};\n      max-width: ${value};\n      box-sizing: border-box;`;

export const buildFlexSectionColumnStylesheet = (section, section_column) => {
  const structure = section_column.options || {};

  const padding = {
    top: structure["padding-top"] != null ? structure["padding-top"] : 10,
    right: structure["padding-right"] != null ? structure["padding-right"] : 10,
    bottom:
      structure["padding-bottom"] != null ? structure["padding-bottom"] : 10,
    left: structure["padding-left"] != null ? structure["padding-left"] : 10,
  };

  const borderStyle = structure["border-style"] || "none";
  const borderColor = structure["border-color"] || "#000";
  const borderWidths = [
    pxValue(structure["border-width-top"]) || "0px",
    pxValue(structure["border-width-right"]) || "0px",
    pxValue(structure["border-width-bottom"]) || "0px",
    pxValue(structure["border-width-left"]) || "0px",
  ].join(" ");

  const overlayColor = structure["color"];
  const overlayOpacity =
    structure["opacity"] != null ? structure["opacity"] : "0";

  const backgroundImage = sanitizeCssUrl(structure["background"]);
  const backgroundPosition = toCssBackgroundPosition(
    structure["background-position"]
  );
  const backgroundSize = toCssBackgroundSize(structure["background-size"]);
  const customBackgroundTop =
    structure["background-position"] === "custom"
      ? structure["background-custom-top-position"]
      : undefined;
  const customBackgroundLeft =
    structure["background-position"] === "custom"
      ? structure["background-custom-left-position"]
      : undefined;

  const columnSelector = `#section${section.id} .cf-flex-column[data-section-column-id='${section_column.id}']`;
  const overlaySelector = `${columnSelector} > .cf-background-overlay`;

  const sectionStructure = (section.options && section.options.structure) || {};

  const { desktop: widthBasis, mobile: widthBasisMobile } =
    flexColumnWidthBasis(structure);

  const flexRule = flexRuleFor(widthBasis);

  const resolveMinHeight = (rawValue, browserHeightKey) =>
    sectionStructure[browserHeightKey] !== "true" &&
    rawValue !== undefined &&
    rawValue !== null &&
    rawValue !== "" &&
    Number(rawValue) > 0
      ? `${rawValue}px`
      : undefined;

  const columnMinHeight = resolveMinHeight(
    sectionStructure["column-min-height"],
    "browser-height"
  );
  const columnMinHeightMobile = resolveMinHeight(
    sectionStructure["column-min-height-mobile"],
    "browser-height-mobile"
  );

  const desktopStyles = `
    ${columnSelector} {
      padding: ${padding.top}px ${padding.right}px ${padding.bottom}px ${
    padding.left
  }px;
      float: none;
      ${flexRule}
      ${columnMinHeight ? `min-height: ${columnMinHeight};` : ""}
      order: ${section_column.position || 0};
      border-style: ${borderStyle};
      border-color: ${borderColor};
      border-width: ${borderWidths};
      ${backgroundImage ? `background-image: url("${backgroundImage}");` : ""}
      ${backgroundImage ? `background-size: ${backgroundSize};` : ""}
      ${
        backgroundImage && backgroundPosition
          ? `background-position: ${backgroundPosition};`
          : ""
      }
      ${
        backgroundImage && customBackgroundTop
          ? `background-position-y: ${customBackgroundTop}px;`
          : ""
      }
      ${
        backgroundImage && customBackgroundLeft
          ? `background-position-x: ${customBackgroundLeft}px;`
          : ""
      }
      ${backgroundImage ? "background-repeat: no-repeat;" : ""}
      position: relative;
    }
    ${overlaySelector} {
      ${overlayColor ? `background: ${overlayColor};` : ""}
      opacity: ${overlayOpacity};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
    }
    ${columnSelector} > .cf-cta-item-container {
      position: relative;
      z-index: 1;
    }
  `;

  // Mobile sizing precedence: an explicit width-mobile always wins; then an
  // explicit columns-per-row-mobile makes columns grow to share each row;
  // then mobile responsive (default on) stacks them full-width; toggled off,
  // mobile inherits the desktop sizing.
  const mobileResponsive =
    sectionStructure["mobile-responsive"] !== "false" &&
    sectionStructure["mobile-responsive"] !== false;

  const mobilePerRow = parseInt(sectionStructure["columns-per-row-mobile"], 10);
  const hasMobileWidthOverride =
    !widthBasisMobile.grow &&
    normalizeFlexWidthValue(structure["width-mobile"]) !== null;

  let mobileBasis;
  if (hasMobileWidthOverride) {
    mobileBasis = widthBasisMobile;
  } else if (!Number.isNaN(mobilePerRow) && mobilePerRow > 1) {
    mobileBasis = { value: null, grow: true };
  } else if (!Number.isNaN(mobilePerRow) && mobilePerRow === 1) {
    mobileBasis = { value: "100%", grow: false };
  } else if (mobileResponsive) {
    mobileBasis = { value: "100%", grow: false };
  } else {
    mobileBasis = widthBasisMobile;
  }

  const mobileRule = flexRuleFor(mobileBasis);

  const mobileMinHeightRule = columnMinHeightMobile
    ? `min-height: ${columnMinHeightMobile};`
    : "";

  const mobilePaddingRule = ["top", "right", "bottom", "left"]
    .map((side) => {
      const value = structure[`padding-mobile-${side}`];
      return value != null && value !== ""
        ? `padding-${side}: ${value}px;`
        : "";
    })
    .filter(Boolean)
    .join("\n        ");

  return `
    ${desktopStyles}
    @media (max-width: 599px) {
      ${columnSelector} {
        ${mobileRule}
        ${mobileMinHeightRule}
        ${mobilePaddingRule}
      }
    }
    #builder .preview-mobile ${columnSelector} {
      ${mobileRule}
      ${mobileMinHeightRule}
      ${mobilePaddingRule}
    }
  `;
};
